<template>
  <div class="totp-setup">
    <!-- Step 1: QR Code Scan & Verify -->
    <div v-if="step === 'scan'" class="totp-setup__step">
      <div class="totp-setup__header">
        <p class="totp-setup__subtitle">
          Scan the QR code below with your authenticator app
        </p>
      </div>

      <!-- QR Code Display -->
      <div class="totp-setup__qr-container">
        <div class="totp-setup__qr-wrapper">
          <qrcode-vue
            v-if="uri"
            :value="uri"
            :size="200"
            level="M"
            class="totp-setup__qr"
          />
        </div>
      </div>

      <!-- Manual Entry Toggle -->
      <div class="totp-setup__manual-toggle">
        <button
          type="button"
          class="totp-setup__toggle-btn"
          @click="toggleManualEntry"
        >
          {{ showManualEntry ? 'Hide manual entry' : "Can't scan? Enter manually" }}
        </button>
      </div>

      <!-- Manual Entry Section -->
      <transition name="slide-fade">
        <div v-if="showManualEntry" class="totp-setup__manual-section">
          <div class="totp-setup__manual-field">
            <p class="totp-setup__manual-label">Account name</p>
            <p class="totp-setup__manual-value">{{ email }}</p>
          </div>
          <div class="totp-setup__manual-field">
            <p class="totp-setup__manual-label">Your secret key</p>
            <div class="totp-setup__secret-row">
              <code class="totp-setup__secret-code">{{ secret }}</code>
              <v-btn
                icon
                small
                class="totp-setup__copy-btn"
                :aria-label="copied ? 'Copied!' : 'Copy secret'"
                @click="handleCopySecret"
              >
                <v-icon :size="16">
                  {{ copied ? 'mdi-check-circle' : 'mdi-content-copy' }}
                </v-icon>
              </v-btn>
            </div>
          </div>
        </div>
      </transition>

      <!-- Verification Input -->
      <div class="totp-setup__verification">
        <label for="verification-code" class="totp-setup__input-label">
          Verification Code
        </label>
        <input
          id="verification-code"
          v-model="verificationCode"
          type="text"
          inputmode="numeric"
          placeholder="000000"
          maxlength="6"
          :disabled="isVerifying"
          class="totp-setup__input"
          autocomplete="off"
          @input="handleCodeInput"
          @keypress.enter="handleVerify"
        />
        <p class="totp-setup__input-hint">
          Enter the 6-digit code from your authenticator app
        </p>
        <transition name="fade">
          <p v-if="error" class="totp-setup__error">{{ error }}</p>
        </transition>
      </div>

      <!-- Action Buttons -->
      <div class="totp-setup__actions">
        <v-btn
          outlined
          large
          class="totp-setup__btn totp-setup__btn--cancel"
          :disabled="isVerifying"
          @click="handleCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          large
          class="totp-setup__btn totp-setup__btn--continue"
          :disabled="isVerifying || verificationCode.length !== 6"
          :loading="isVerifying"
          @click="handleVerify"
        >
          {{ isVerifying ? 'Verifying...' : 'Continue' }}
        </v-btn>
      </div>
    </div>

    <!-- Step 2: Backup Codes -->
    <div v-if="step === 'backup-codes'" class="totp-setup__step">
      <div class="totp-setup__header">
        <div class="totp-setup__icon-wrapper">
          <v-icon color="primary" :size="32">mdi-shield-check</v-icon>
        </div>
        <h2 class="totp-setup__title">Save Your Backup Codes</h2>
        <p class="totp-setup__subtitle">
          If you lose access to your authenticator app, you can use these codes to sign in.
          Each code can only be used once.
        </p>
      </div>

      <!-- Backup Codes Grid -->
      <div class="totp-setup__codes-container">
        <div class="totp-setup__codes-grid">
          <div
            v-for="(code, index) in backupCodes"
            :key="index"
            class="totp-setup__code-item"
          >
            {{ code }}
          </div>
        </div>
      </div>

      <!-- Copy/Download Actions -->
      <div class="totp-setup__code-actions">
        <v-btn
          outlined
          large
          class="totp-setup__btn totp-setup__btn--code-action"
          @click="handleCopyBackupCodes"
        >
          <v-icon left :size="16">
            {{ backupCodesCopied ? 'mdi-check-circle' : 'mdi-content-copy' }}
          </v-icon>
          {{ backupCodesCopied ? 'Copied!' : 'Copy Codes' }}
        </v-btn>
        <v-btn
          outlined
          large
          class="totp-setup__btn totp-setup__btn--code-action"
          @click="handleDownloadBackupCodes"
        >
          <v-icon left :size="16">mdi-download</v-icon>
          Download
        </v-btn>
      </div>

      <!-- Acknowledgement Checkbox -->
      <div class="totp-setup__acknowledgement">
        <v-checkbox
          v-model="acknowledgedSaved"
          class="totp-setup__checkbox"
          color="warning"
        >
          <template #label>
            <span class="totp-setup__acknowledgement-text">
              I have saved these backup codes in a secure location. I understand that without
              these codes, I may lose access to my account if I lose my authenticator device.
            </span>
          </template>
        </v-checkbox>
      </div>

      <!-- Complete Button -->
      <v-btn
        color="primary"
        large
        block
        class="totp-setup__btn totp-setup__btn--complete"
        :disabled="!acknowledgedSaved"
        @click="handleCompleteSetup"
      >
        Complete Setup
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import QrcodeVue from 'qrcode.vue'

type SetupStep = 'scan' | 'backup-codes'

/**
 * TOTPSetup - Handles the initial TOTP enrolment flow
 *
 * Flow:
 * 1. Display QR code for scanning with authenticator app
 * 2. User enters verification code from app
 * 3. Show backup codes that user must save
 * 4. Complete setup after user acknowledges saving codes
 *
 * NOTE: This component uses MOCK data for development/testing.
 * Production implementation will:
 * - Call backend API to generate TOTP secret
 * - Verify codes via backend validation
 * - Store TOTP secret and backup codes securely in database
 * - Integrate with authentication service
 */
export default Vue.extend({
  name: 'TOTPSetup',

  components: {
    QrcodeVue
  },

  props: {
    /** User's email address for TOTP setup */
    email: {
      type: String,
      required: true
    },
    /** Callback when setup is complete */
    onComplete: {
      type: Function,
      required: true
    },
    /** Callback when user cancels setup */
    onCancel: {
      type: Function,
      required: true
    }
  },

  data() {
    return {
      step: 'scan' as SetupStep,
      // MOCK DATA: In production, these will come from backend API
      secret: 'JBSWY3DPEHPK3PXP' as string,
      uri: '' as string,
      backupCodes: [
        '1234-5678-90AB',
        'CDEF-1234-5678',
        '90AB-CDEF-1234',
        '5678-90AB-CDEF',
        '1234-CDEF-5678',
        '90AB-1234-CDEF',
        '5678-CDEF-90AB',
        'CDEF-5678-1234',
        '90AB-5678-CDEF',
        '1234-90AB-5678'
      ] as string[],
      verificationCode: '' as string,
      error: '' as string,
      isVerifying: false as boolean,
      showManualEntry: false as boolean,
      copied: false as boolean,
      backupCodesCopied: false as boolean,
      acknowledgedSaved: false as boolean
    }
  },

  mounted() {
    // Generate TOTP URI for QR code
    // Format: otpauth://totp/CassieCloud:email?secret=SECRET&issuer=CassieCloud
    this.uri = `otpauth://totp/CassieCloud:${this.email}?secret=${this.secret}&issuer=CassieCloud`
  },

  methods: {
    toggleManualEntry() {
      this.showManualEntry = !this.showManualEntry
    },

    async handleCopySecret() {
      try {
        await navigator.clipboard.writeText(this.secret)
        this.copied = true
        setTimeout(() => {
          this.copied = false
        }, 2000)
      } catch (err) {
        console.error('Failed to copy secret:', err)
      }
    },

    async handleCopyBackupCodes() {
      try {
        const text = this.backupCodes.join('\n')
        await navigator.clipboard.writeText(text)
        this.backupCodesCopied = true
        setTimeout(() => {
          this.backupCodesCopied = false
        }, 2000)
      } catch (err) {
        console.error('Failed to copy backup codes:', err)
      }
    },

    handleDownloadBackupCodes() {
      const text =
        `Syrenis Backup Codes for ${this.email}\n` +
        `Generated: ${new Date().toISOString()}\n\n` +
        `Keep these codes safe. Each code can only be used once.\n\n` +
        this.backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')

      const blob = new Blob([text], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `syrenis-backup-codes-${this.email.split('@')[0]}.txt`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    handleCodeInput(event: Event) {
      const target = event.target as HTMLInputElement
      // Only allow digits, max 6 characters
      const value = target.value.replace(/\D/g, '').slice(0, 6)
      this.verificationCode = value
      this.error = ''
    },

    async handleVerify() {
      if (!this.verificationCode || this.verificationCode.length !== 6) {
        this.error = 'Please enter a 6-digit code'
        return
      }

      this.isVerifying = true
      this.error = ''

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 500))

      // MOCK VALIDATION: In production, this will call backend API
      // Accept any 6-digit code for demo purposes
      const isValid = /^\d{6}$/.test(this.verificationCode)

      if (isValid) {
        // Move to backup codes step
        this.step = 'backup-codes'
      } else {
        this.error = 'Invalid code. Please check your authenticator app and try again.'
        this.verificationCode = ''
      }

      this.isVerifying = false
    },

    handleCompleteSetup() {
      // PRODUCTION: Will call backend API to:
      // - Store TOTP secret
      // - Store backup codes (hashed)
      // - Enable 2FA for user account
      this.onComplete()
    },

    handleCancel() {
      this.onCancel()
    }
  }
})
</script>

<style scoped>
.totp-setup {
  max-width: 480px;
  margin: 0 auto;
}

.totp-setup__step {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.totp-setup__header {
  text-align: center;
}

.totp-setup__icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.totp-setup__title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.totp-setup__subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* QR Code */
.totp-setup__qr-container {
  display: flex;
  justify-content: center;
}

.totp-setup__qr-wrapper {
  padding: 16px;
  background: #ffffff;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  display: inline-flex;
}

.totp-setup__qr {
  display: block;
}

/* Manual Entry Toggle */
.totp-setup__manual-toggle {
  text-align: center;
}

.totp-setup__toggle-btn {
  font-size: 0.875rem;
  color: #6b7280;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s ease;
}

.totp-setup__toggle-btn:hover {
  color: #1f2937;
}

/* Manual Entry Section */
.totp-setup__manual-section {
  padding: 16px;
  background: #f9fafb;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.totp-setup__manual-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.totp-setup__manual-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.totp-setup__manual-value {
  font-size: 0.875rem;
  color: #1f2937;
  font-family: 'Courier New', monospace;
  margin: 0;
}

.totp-setup__secret-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.totp-setup__secret-code {
  flex: 1;
  font-size: 0.875rem;
  color: #1f2937;
  font-family: 'Courier New', monospace;
  background: #ffffff;
  padding: 12px;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  word-break: break-all;
}

.totp-setup__copy-btn {
  flex-shrink: 0;
}

/* Verification Input */
.totp-setup__verification {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.totp-setup__input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.totp-setup__input {
  width: 100%;
  padding: 12px 16px;
  font-size: 1.5rem;
  font-family: 'Courier New', monospace;
  text-align: center;
  letter-spacing: 0.5em;
  border: 2px solid #e5e7eb;
  border-radius: var(--radius);
  background-color: #ffffff;
  color: #1f2937;
  outline: none;
  transition: border-color 0.2s ease;
}

.totp-setup__input:focus {
  border-color: var(--primary, #4a7c9b);
}

.totp-setup__input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.totp-setup__input-hint {
  font-size: 0.75rem;
  color: #6b7280;
  text-align: center;
  margin: 0;
}

.totp-setup__error {
  font-size: 0.875rem;
  font-weight: 500;
  color: #dc2626;
  margin: 0;
}

/* Action Buttons */
.totp-setup__actions {
  display: flex;
  gap: 12px;
}

.totp-setup__btn {
  flex: 1;
  text-transform: none;
  font-weight: 500;
}

.totp-setup__btn--cancel {
  color: #6b7280;
}

/* Backup Codes */
.totp-setup__codes-container {
  background: #f9fafb;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  padding: 16px;
}

.totp-setup__codes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.totp-setup__code-item {
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
  text-align: center;
  padding: 10px 12px;
  background: #ffffff;
  border-radius: var(--radius);
  border: 1px solid #e5e7eb;
  color: #1f2937;
}

.totp-setup__code-actions {
  display: flex;
  gap: 12px;
}

.totp-setup__btn--code-action {
  flex: 1;
}

/* Acknowledgement */
.totp-setup__acknowledgement {
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: var(--radius);
  padding: 12px 16px;
}

.totp-setup__checkbox {
  margin: 0;
}

.totp-setup__acknowledgement-text {
  font-size: 0.875rem;
  color: #92400e;
  line-height: 1.5;
}

.totp-setup__btn--complete {
  text-transform: none;
  font-weight: 500;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Dark mode support */
.theme--dark .totp-setup__title {
  color: #f9fafb;
}

.theme--dark .totp-setup__subtitle {
  color: #9ca3af;
}

.theme--dark .totp-setup__qr-wrapper {
  background: #374151;
  border-color: #4b5563;
}

.theme--dark .totp-setup__manual-section {
  background: #1f2937;
  border-color: #374151;
}

.theme--dark .totp-setup__manual-value,
.theme--dark .totp-setup__secret-code,
.theme--dark .totp-setup__code-item {
  background: #111827;
  color: #f9fafb;
  border-color: #374151;
}

.theme--dark .totp-setup__input {
  background: #1f2937;
  border-color: #374151;
  color: #f9fafb;
}

.theme--dark .totp-setup__input:focus {
  border-color: var(--primary-light, #6a9cb8);
}

.theme--dark .totp-setup__codes-container {
  background: #1f2937;
  border-color: #374151;
}

.theme--dark .totp-setup__acknowledgement {
  background: rgba(251, 191, 36, 0.1);
  border-color: #d97706;
}

.theme--dark .totp-setup__acknowledgement-text {
  color: #fbbf24;
}
</style>
